import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const siteUrl = 'https://www.littleadda.com';
const defaultImage = `${siteUrl}/images/little-adda-parent-hero.png`;

const faqItems = [
  ['What is Little Adda?', 'Little Adda is a family copilot that finds actionable information in school emails and drafts reminders, tasks, and calendar suggestions for parent review.'],
  ['Does Little Adda add events automatically?', 'No. Suggested reminders, tasks, and calendar items wait for a parent to approve, edit, or dismiss them.'],
  ['Who is Little Adda for?', 'Little Adda is designed for parents and caregivers coordinating school communications and family responsibilities.'],
  ['Is Little Adda affiliated with my school?', 'Little Adda is a parent-focused organization product and is not presented as a school or school-district service.'],
];

export const publicPageMetadata = {
  '/': {
    title: 'Little Adda — School-email intelligence for parents',
    description: 'Little Adda turns school emails into parent-approved reminders, tasks, and calendar suggestions so families can act without losing control.',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'Little Adda',
      url: `${siteUrl}/`,
      applicationCategory: 'LifestyleApplication',
      operatingSystem: 'Web',
      description: 'A family copilot that turns school emails into parent-approved reminders, tasks, and calendar suggestions.',
      audience: { '@type': 'Audience', audienceType: 'Parents and caregivers' },
    },
  },
  '/school-email-assistant': {
    title: 'School Email Assistant for Parents | Little Adda',
    description: 'Find dates, forms, fees, supplies, and schedule changes in school emails, then review suggested family actions before anything is added.',
  },
  '/family-calendar-app': {
    title: 'Family Calendar Suggestions from School Email | Little Adda',
    description: 'Turn school dates and schedule changes into parent-reviewed calendar suggestions that caregivers can coordinate around.',
  },
  '/shared-family-task-manager': {
    title: 'Shared Family Tasks for School Responsibilities | Little Adda',
    description: 'Turn school requests into clear, parent-approved family tasks so caregivers know what needs doing and who owns the next step.',
  },
  '/faq': {
    title: 'Little Adda FAQ — School Email, Parent Approval, and Family Planning',
    description: 'Answers about how Little Adda reads school updates, suggests actions, keeps parents in control, and supports family coordination.',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqItems.map(([name, text]) => ({
        '@type': 'Question',
        name,
        acceptedAnswer: { '@type': 'Answer', text },
      })),
    },
  },
  '/about': {
    title: 'About Little Adda — A Family Copilot for Parents',
    description: 'Learn why Little Adda is being built to help parents turn scattered school communication into calm, approved family action.',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: 'About Little Adda',
      url: `${siteUrl}/about`,
      mainEntity: {
        '@type': 'Organization',
        name: 'Little Adda',
        url: `${siteUrl}/`,
        description: 'Little Adda builds parent-controlled tools for organizing school communication and family responsibilities.',
      },
    },
  },
  '/privacy-and-security': {
    title: 'Privacy and Security at Little Adda',
    description: 'Understand Little Adda’s parent-control principles, authenticated workspace boundaries, and approach to handling family information.',
  },
};

function setMeta(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value));
}

function SiteMetadata() {
  const { pathname } = useLocation();

  useEffect(() => {
    const normalizedPathname = pathname === '/' ? '/' : pathname.replace(/\/+$/, '');
    const metadata = publicPageMetadata[normalizedPathname];
    const isPublicPage = Boolean(metadata);
    const title = metadata?.title || 'Little Adda App';
    const description = metadata?.description || 'Private Little Adda family workspace.';
    const canonicalUrl = isPublicPage ? `${siteUrl}${normalizedPathname === '/' ? '/' : normalizedPathname}` : null;

    document.title = title;
    setMeta('meta[name="description"]', { name: 'description', content: description });
    setMeta('meta[name="robots"]', {
      name: 'robots',
      content: isPublicPage ? 'index, follow, max-image-preview:large, max-snippet:-1' : 'noindex, nofollow',
    });
    setMeta('meta[property="og:title"]', { property: 'og:title', content: title });
    setMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    setMeta('meta[property="og:image"]', { property: 'og:image', content: defaultImage });
    setMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
    setMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (canonicalUrl) {
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
      }
      canonical.href = canonicalUrl;
      setMeta('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl });
    } else {
      canonical?.remove();
      document.head.querySelector('meta[property="og:url"]')?.remove();
    }

    const structuredData = document.getElementById('structured-data');
    if (metadata?.schema) {
      const script = structuredData || document.createElement('script');
      script.id = 'structured-data';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(metadata.schema);
      if (!structuredData) document.head.appendChild(script);
    } else {
      structuredData?.remove();
    }
  }, [pathname]);

  return null;
}

export default SiteMetadata;
